import { useMemo } from 'preact/hooks';

import cx from 'clsx';

import { Skeleton } from '@shared/components/core';
import { useIsMobile } from '@shared/hooks/useIsMobile';

import styles from './PageSkeleton.module.scss';

const LeftPanelSkeleton = () =>
	useMemo(
		() => (
			<div className={cx('p2 height100 flex-shrink0', styles.leftPanelWrapper)}>
				<div
					className={cx(
						'd-flex flex-column items-center gap6 height100 border-radius4 border1 border-grey2 pt8 bg-white overflow-hidden',
						styles.leftPanel,
					)}
				>
					<div className="d-flex items-center gap3 width100 pl6 pr2 flex-shrink0">
						<Skeleton
							height={40}
							width={40}
							className="border-radius-full flex-shrink0"
							style={{ borderRadius: '50%', flexShrink: 0 }}
						/>
						<Skeleton height={22} width={184} />
					</div>

					<div
						className={cx(
							'd-flex flex-column flex1 width100 gap6 py0 px4',
							styles.leftContent,
						)}
					>
						<div
							className={cx(
								'd-flex items-center justify-between p4 border1 border-grey2 border-radius3 card-shadow1 flex-shrink0',
								styles.amountCard,
							)}
						>
							<Skeleton height={22} width={80} />
							<Skeleton height={22} width={120} />
						</div>

						<div
							className={cx(
								'd-flex flex-column p4 gap1 border-radius2 flex-shrink0',
								styles.payingForCard,
							)}
						>
							<Skeleton height={12} width={90} className="mb2" />
							<Skeleton height={16} className="mb1" />
							<Skeleton height={16} className="mb1" />
							<Skeleton height={16} width={180} />
						</div>
					</div>

					<div
						className={cx(
							'd-flex items-center justify-center width100 px4 py6 border-top1 border-grey2 flex-shrink0',
							styles.brandingFooter,
						)}
					>
						<Skeleton height={20} width={120} />
					</div>
				</div>
			</div>
		),
		[],
	);

const RightPanelSkeleton = () =>
	useMemo(
		() => (
			<div className={cx('flex1 d-flex flex-column pt8 pr8 pb8 pl0')}>
				<div
					className={cx(
						'flex1 d-flex flex-column border1 border-grey2 border-radius4 bg-white',
					)}
				>
					<div
						className={cx(
							'd-flex flex-column items-center flex1 width100',
							styles.rightContent,
						)}
					>
						<div className="d-flex flex-column width100 gap2 pt0 px4 flex-shrink0">
							<Skeleton height={32} width={280} className="mb2" />
							<Skeleton height={20} width={340} />
						</div>

						<div
							className={cx(
								'd-flex flex-column bg-white width100 gap10 border1 card-shadow1 border-radius4 grey1 flex-shrink0',
								styles.formCard,
							)}
						>
							<div className="d-flex flex-column gap2">
								<Skeleton height={16} width={140} />
								<Skeleton height={48} />
							</div>
							<Skeleton height={48} />
						</div>
					</div>
				</div>
			</div>
		),
		[],
	);

const MobilePageSkeleton = () =>
	useMemo(
		() => (
			<div
				className={cx(
					'bg-tertiary d-flex flex-column',
					styles.mobilePageWrapper,
				)}
			>
				{/* ── Top: PaymentInfo ── */}
				<div className="relative flex1">
					<div className={cx('px4 py width100')}>
						<div className="d-flex flex-column gap4">
							{/* Logo + merchant name */}
							<div className="d-flex items-center gap3">
								<Skeleton
									height={40}
									width={40}
									style={{ borderRadius: '50%', flexShrink: 0 }}
								/>
								<Skeleton height={20} width={140} />
							</div>

							{/* Amount card */}
							<div
								className={cx(
									'd-flex items-center justify-between p4 border1 border-grey2 border-radius3 card-shadow1 bg-white',
								)}
							>
								<Skeleton height={20} width={70} />
								<Skeleton height={20} width={100} />
							</div>

							{/* Description block */}
							<div className="d-flex flex-column gap1">
								<Skeleton height={12} width={80} className="mb2" />
								<Skeleton height={14} />
								<Skeleton height={14} />
								<Skeleton height={14} width={160} />
							</div>
						</div>
					</div>

					{/* ── Bottom: InputCard overlay ── */}
					<div
						className={cx(
							'absolute bottom0 left0 width100',
							styles.mobileInputCard,
						)}
					>
						<div
							className={cx(
								'd-flex flex-column border-top-radius4 bg-white px4 pt4 pb3 gap8',
								styles.mobileBottomCard,
							)}
						>
							{/* Title + subtitle */}
							<div className="d-flex flex-column gap2">
								<Skeleton height={22} width={200} />
								<Skeleton height={16} width={260} />
							</div>

							{/* Input + button */}
							<div className="d-flex flex-column gap2">
								<Skeleton height={48} />
								<Skeleton height={48} />
							</div>
						</div>
					</div>
				</div>

				{/* ── Footer ── */}
				<div className="py3 px4 d-flex justify-center flex-shrink0">
					<Skeleton height={20} width={120} className="mx-auto" />
				</div>
			</div>
		),
		[],
	);

const PageSkeleton = () => {
	const isMobile = useIsMobile();

	return useMemo(
		() =>
			isMobile ? (
				<MobilePageSkeleton />
			) : (
				<div
					className={cx(
						'relative d-flex items-center justify-center bg-tertiary',
						styles.pageWrapper,
					)}
				>
					<div
						className={cx(
							'd-flex',
							'border-radius6 overflow-hidden',
							styles.card,
						)}
					>
						<LeftPanelSkeleton />
						<RightPanelSkeleton />
					</div>
				</div>
			),
		[isMobile],
	);
};

export default PageSkeleton;
